import { defineStore } from 'pinia';
import { ref } from 'vue';
import { authApi, type MeResponse } from '../api/auth';
import { meApi } from '../api/me';
import { configApi, type PublicConfig } from '../api/config';
import { ApiError } from '../api/client';

export const useAuthStore = defineStore('auth', () => {
  const me = ref<MeResponse | null>(null);
  const config = ref<PublicConfig | null>(null);
  const loading = ref(false);
  const ready = ref(false);

  async function bootstrap(): Promise<void> {
    loading.value = true;
    try {
      // Public config never requires auth.
      const cfgPromise = configApi.get().catch(() => null);
      try {
        me.value = await authApi.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          me.value = null;
        } else {
          // Surface non-auth errors but don't block the SPA from rendering Login.
          // eslint-disable-next-line no-console
          console.warn('auth:me failed', err);
          me.value = null;
        }
      }
      config.value = await cfgPromise;
    } finally {
      loading.value = false;
      ready.value = true;
    }
  }

  async function refresh(): Promise<void> {
    try {
      me.value = await authApi.me();
    } catch {
      me.value = null;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch {
      // Ignore — clear local state anyway.
    }
    me.value = null;
  }

  /**
   * Update the signed-in user's display name and patch local state.
   * Calls PATCH /api/me; on success updates me.displayName so the sidebar
   * and other components reflect the change without a full refresh.
   */
  async function updateDisplayName(displayName: string | null): Promise<void> {
    const updated = await meApi.updateProfile(displayName);
    if (me.value) {
      me.value.displayName = updated.displayName;
    }
  }

  return { me, config, loading, ready, bootstrap, refresh, logout, updateDisplayName };
});
